import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Paper, styled, Typography } from "~/common/mui";
import { ChildCategoryNavigationCarousel } from "~/features/categoryNavigation/components/ChildCategoryNavigationCarousel";
import type { RootState } from "~/app/store";
import type { NavigationNode } from "~/domains/navigation/types/NavigationTypes";
import { C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";
import { selectL1Categories } from "../redux/filterConfigSelectors";
import { setSelectedProductGroupsThunk } from "../redux/actions";
import {
  IMG_CATEGORY_BEARINGS,
  IMG_CATEGORY_DIRECT_DRIVES,
  IMG_CATEGORY_HOUSINGS,
  IMG_CATEGORY_LINEAR,
  IMG_CATEGORY_SERVICE,
} from "~/utils/url/staticResources";

const Root = styled(Paper)(({ theme }) => ({
  background: C_SURFACE_BRIGHT,
  padding: "16px",
  [theme.breakpoints.up("desktop-m")]: {
    padding: "24px",
  },
}));

interface TreeNode extends NavigationNode {
  childNodes: TreeNode[];
}

const NODE_ID_TO_IMAGE_MAPPING: Record<string, string> = {
  RollingBearings: IMG_CATEGORY_BEARINGS,
  DirectDrives: IMG_CATEGORY_DIRECT_DRIVES,
  Housings: IMG_CATEGORY_HOUSINGS,
  LinearMotion: IMG_CATEGORY_LINEAR,
  DigitalServiceSolutions: IMG_CATEGORY_SERVICE,
};

export const L1CategoryNavigation = () => {
  const { t } = useTranslation();

  const isAnyCategorySelected = useSelector((state: RootState) => state.srp.facets.selectedProductGroups.length > 0);

  const l1Categories = useSelector(selectL1Categories);
  const dispatch = useDispatch();

  if (isAnyCategorySelected || !l1Categories || l1Categories.length === 0) {
    return null;
  }

  const categoryData = l1Categories.map((category) => {
    return {
      title: category.name,
      imageUrl: NODE_ID_TO_IMAGE_MAPPING[category.treeNodeUid] ?? "",
      onClick: () => dispatch(setSelectedProductGroupsThunk(category.fullFacetUid)),
    };
  });

  return (
    <Root>
      <Typography variant="titleLarge" sx={{ mb: 2 }}>
        {t("page.srp.selectCategory.title", "Select a product group")}
      </Typography>
      <ChildCategoryNavigationCarousel items={categoryData} />
    </Root>
  );
};
